import api from '../api';

export interface ExportOptions {
  timeout?: number;
  onProgress?: (progress: number) => void;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
}

class ExportService {
  /**
   * Export inventory data to Excel format
   */
  async exportInventory(options: ExportOptions = {}): Promise<ExportResult> {
    const { timeout = 60000 } = options;

    try {
      const response = await api.get('/inventory/export', {
        responseType: 'blob',
        timeout,
        onDownloadProgress: (progressEvent) => {
          if (options.onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            options.onProgress(progress);
          }
        }
      });

      // Create blob from response
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `inventory_export_${dateStr}.xlsx`;

      return { blob, filename };
    } catch (error: any) {
      // Enhanced error handling
      if (error.response?.status === 500) {
        throw new Error('Server error during export. Please contact support if this persists.');
      } else if (error.response?.status === 404) {
        throw new Error('Export endpoint not found. Please contact support.');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Export timed out. The dataset might be too large.');
      } else if (error.response?.data) {
        try {
          // Try to parse error response if it's JSON
          const reader = new FileReader();
          const errorText = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsText(error.response.data);
          });
          
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.detail || 'Export failed. Please try again.');
        } catch (parseError) {
          // If parsing fails, use generic message
          throw new Error('Export failed. Please try again.');
        }
      } else {
        throw new Error('Export failed. Please check your connection and try again.');
      }
    }
  }

  /**
   * Download a blob as a file
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export and automatically download inventory data
   */
  async exportAndDownload(options: ExportOptions = {}): Promise<string> {
    const { blob, filename } = await this.exportInventory(options);
    this.downloadFile(blob, filename);
    return filename;
  }
}

// Export singleton instance
export const exportService = new ExportService();
export default exportService;